import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--primary-600);
  text-decoration: none;
  margin-bottom: var(--space-xl);
  
  &:hover {
    color: var(--primary-700);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Content = styled.div`
  line-height: 1.8;
  font-size: 1.125rem;
  color: var(--secondary-700);

  h2 {
    margin: var(--space-2xl) 0 var(--space-lg);
    color: var(--secondary-900);
    font-size: 1.75rem;
  }

  h3 {
    margin: var(--space-xl) 0 var(--space-md);
    color: var(--secondary-900);
    font-size: 1.25rem;
  }

  p {
    margin-bottom: var(--space-lg);
  }

  ul, ol {
    margin-bottom: var(--space-lg);
    padding-left: var(--space-xl);
  }

  li {
    margin-bottom: var(--space-sm);
  }

  strong {
    color: var(--secondary-900);
    font-weight: 600;
  }

  .contact-info {
    background: var(--secondary-50);
    padding: var(--space-lg);
    border-radius: var(--radius-md);
    margin: var(--space-lg) 0;
    border-left: 4px solid var(--primary-600);
  }

  .last-updated {
    font-style: italic;
    color: var(--secondary-500);
    margin-top: var(--space-2xl);
    padding-top: var(--space-lg);
    border-top: 1px solid var(--secondary-200);
  }
`;

const TermsOfService = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        title={t('terms.title')}
        description="Terms of Service for ScrumNX - Learn about the terms and conditions for using our services."
        keywords="Terms of Service, Terms and Conditions, Legal, Service Agreement"
      />
      <Breadcrumbs />
      <Container>
        <BackLink to="/">
          <FaArrowLeft /> {t('commonPages.backToHome')}
        </BackLink>

        <Title>{t('terms.title')}</Title>

        <Content>
          <p>
            {t('terms.intro')}
          </p>

          <h2>1. {t('terms.acceptance')}</h2>

          <p>
            {t('terms.acceptanceText')}
          </p>

          <h2>2. {t('terms.servicesDescription')}</h2>

          <p>
            {t('terms.servicesDescriptionText')}
          </p>
          <ul>
            <li>{t('imprint.softwareDevelopment')}</li>
            <li>{t('imprint.devopsAutomation')}</li>
            <li>{t('imprint.qaTesting')}</li>
            <li>{t('imprint.itArchitecture')}</li>
          </ul>

          <h2>3. {t('terms.userResponsibilities')}</h2>

          <h3>3.1 {t('terms.accurateInformation')}</h3>
          <p>
            {t('terms.accurateInformationText')}
          </p>

          <h3>3.2 {t('terms.acceptableUse')}</h3>
          <p>
            {t('terms.acceptableUseText')}
          </p>
          <ul>
            <li>{t('terms.illegalPurpose')}</li>
            <li>{t('terms.interfere')}</li>
            <li>{t('terms.unauthorizedAccess')}</li>
            <li>{t('terms.harmfulContent')}</li>
            <li>{t('terms.violateLaws')}</li>
          </ul>

          <h2>4. {t('terms.intellectualProperty')}</h2>

          <h3>4.1 {t('terms.ourContent')}</h3>
          <p>
            {t('terms.ourContentText')}
          </p>

          <h3>4.2 {t('terms.yourContent')}</h3>
          <p>
            {t('terms.yourContentText')}
          </p>

          <h2>5. {t('terms.privacyDataProtection')}</h2>

          <p>
            {t('terms.privacyDataProtectionText')}
          </p>

          <h2>6. {t('terms.serviceAvailability')}</h2>

          <p>
            {t('terms.serviceAvailabilityText')}
          </p>

          <h2>7. {t('terms.limitationLiability')}</h2>

          <p>
            {t('terms.limitationLiabilityText')}
          </p>

          <h2>8. {t('terms.disclaimerWarranties')}</h2>

          <p>
            {t('terms.disclaimerWarrantiesText')}
          </p>
          <ul>
            <li>{t('terms.merchantability')}</li>
            <li>{t('terms.meetRequirements')}</li>
            <li>{t('terms.uninterrupted')}</li>
            <li>{t('terms.accuracy')}</li>
          </ul>

          <h2>9. {t('terms.indemnification')}</h2>

          <p>
            {t('terms.indemnificationText')}
          </p>

          <h2>10. {t('terms.termination')}</h2>

          <p>
            {t('terms.terminationText')}
          </p>

          <h2>11. {t('terms.governingLaw')}</h2>

          <p>
            {t('terms.governingLawText')}
          </p>

          <h2>12. {t('terms.disputeResolution')}</h2>

          <p>
            {t('terms.disputeResolutionText')}
          </p>

          <h2>13. {t('terms.severability')}</h2>

          <p>
            {t('terms.severabilityText')}
          </p>

          <h2>14. {t('terms.entireAgreement')}</h2>

          <p>
            {t('terms.entireAgreementText')}
          </p>

          <h2>15. {t('terms.changesToTerms')}</h2>

          <p>
            {t('terms.changesToTermsText')}
          </p>

          <h2>16. {t('terms.contactInfo')}</h2>

          <div className="contact-info">
            <p><strong>{t('terms.contactInfoText')}</strong></p>
            <p>
              <strong>Beyer & Jdaa IPM Consulting GbR</strong><br />
              Bahnhofstr. 76<br />
              25715 Eddelak<br />
              Deutschland<br />
              {t('common.email')}: info@ipm-consulting.org
            </p>
          </div>

          <div className="last-updated">
            <p><strong>{t('commonPages.lastUpdated')}:</strong> January 15, 2026</p>
          </div>
        </Content>
      </Container>
    </>
  );
};

export default TermsOfService; 